import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const CharacterContext = createContext(null);

const REVERT_DELAY_MS = 2600;

export function CharacterProvider({ children }) {
  const [mood, setMoodState] = useState('idle');
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const revertTimer = useRef(null);

  const clearRevertTimer = useCallback(() => {
    if (revertTimer.current) {
      clearTimeout(revertTimer.current);
      revertTimer.current = null;
    }
  }, []);

  // Sets the mood and leaves it there (e.g. 'thinking' while a request is in flight -
  // the caller is responsible for calling this again once it resolves).
  const setMood = useCallback((nextMood) => {
    clearRevertTimer();
    setMoodState(nextMood);
  }, [clearRevertTimer]);

  // Sets the mood as a brief reaction (happy/sad/surprised to an outcome), then eases
  // back to idle on its own - most callers want this one, not setMood.
  const flashMood = useCallback((nextMood, duration = REVERT_DELAY_MS) => {
    clearRevertTimer();
    setMoodState(nextMood);
    revertTimer.current = setTimeout(() => {
      setMoodState('idle');
      revertTimer.current = null;
    }, duration);
  }, [clearRevertTimer]);

  // Web parity for "user just logged a big transaction": shows a speech bubble near the
  // character even while the chat panel is closed, inviting them to open it - no AI call
  // is made until they actually do.
  const promptBigTransaction = useCallback((expenseName) => {
    clearRevertTimer();
    setMoodState('surprised');
    setPendingPrompt({ text: 'Hey, can I ask you something?', expenseName });
  }, [clearRevertTimer]);

  const dismissPrompt = useCallback(() => {
    setPendingPrompt(null);
    setMoodState('idle');
  }, []);

  const value = useMemo(() => ({
    mood, setMood, flashMood, pendingPrompt, promptBigTransaction, dismissPrompt,
  }), [mood, setMood, flashMood, pendingPrompt, promptBigTransaction, dismissPrompt]);

  return <CharacterContext.Provider value={value}>{children}</CharacterContext.Provider>;
}

export function useCharacterState() {
  const context = useContext(CharacterContext);
  if (!context) throw new Error('useCharacterState must be used within CharacterProvider');
  return context;
}
